import React, { useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Users, Check, CheckCheck, X } from 'lucide-react';

const INVESTORS = [
  { key: 'geq', name: 'GEQ Holding GmbH', share: 10.0 },
  { key: 'olta_vv', name: 'OLTA Vermögensverwaltung GmbH', share: 10.0 },
  { key: 'friedrichs', name: 'Rüdiger Friedrichs', share: 10.0 },
  { key: 'aurica', name: 'Aurica', share: 10.0, note: '2 Vehikel: Aurica Search Fund I FCRE (5,4%) + Aurica Search Fund Capital I FCRE SA (4,6%)' },
  { key: 'halusa', name: 'Lukas Halusa', share: 10.0 },
  { key: 'celium', name: 'Celium Capital Partners S.L.', share: 8.5 },
  { key: 'kalm', name: 'Kalm & Co Holdings Inc.', share: 7.5 },
  { key: 'cabiedes', name: 'Cabiedes Capital, S.L.', share: 7.5 },
  { key: 'turtle', name: 'Turtle Capital SCR SA', share: 5.0 },
  { key: 'bovensmann', name: 'Bovensmann Stiftung 2022', share: 5.0 },
  { key: 'yildiz', name: 'Cavit Yildiz', share: 5.0 },
  { key: 'sertoglu', name: 'Cem Sertoglu', share: 5.0 },
  { key: 'uhaldia', name: 'UHALDIA INVESTMENTS SCR SA', share: 2.5 },
  { key: 'ps_holding', name: 'P&S Holding UG', share: 2.5 },
  { key: 'bnvest', name: 'BNVEST GmbH', share: 1.5 },
];

const nextState = (s) => (s === 'in' ? 'confirmed' : s === 'confirmed' ? 'out' : s === 'out' ? null : 'in');

const BADGE_STYLES = {
  none: 'border-border bg-secondary/30 text-muted-foreground hover:border-foreground/40 hover:text-foreground',
  in: 'border-green-500/60 bg-green-600/20 text-green-400',
  confirmed: 'border-green-200 bg-green-300 text-green-950',
  out: 'border-red-500/40 bg-red-600/15 text-red-400',
};

function ticketFmt(equityM, share) {
  const amt = equityM * (share / 100);
  if (!equityM || amt <= 0) return null;
  return amt < 1 ? `€${Math.round(amt * 1000)}k` : `€${amt.toFixed(2)}m`;
}

export function InvestorAppetiteCard({ projectId, appetite: initial, equity }) {
  const { isAdmin } = useAuth();
  const [appetite, setAppetite] = useState(initial || {});

  const toggle = async (inv) => {
    if (!isAdmin) return;
    const state = nextState(appetite[inv.key]);
    const prev = appetite;
    setAppetite({ ...appetite, [inv.key]: state });
    try {
      const res = await api.updateInvestorAppetite(projectId, { key: inv.key, state });
      setAppetite(res.data.investor_appetite || {});
    } catch (err) {
      setAppetite(prev);
      toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen');
    }
  };

  const sum = (st) => INVESTORS.filter((i) => appetite[i.key] === st).reduce((a, i) => a + i.share, 0);
  const inPct = sum('in');
  const confirmedPct = sum('confirmed');
  const outPct = sum('out');
  const committed = inPct + confirmedPct;
  const openPct = Math.max(100 - committed - outPct, 0);
  const openCount = INVESTORS.filter((i) => !appetite[i.key]).length;
  const confirmedReached = confirmedPct > 50;
  const softReached = committed > 50;

  let statusText, statusCls;
  if (confirmedReached) {
    statusText = `✓ >50% BESTÄTIGT — ${confirmedPct.toFixed(1)}% (+ ${inPct.toFixed(1)}% m. E.)`;
    statusCls = 'border-green-500/50 bg-green-600/15 text-green-400';
  } else if (softReached) {
    statusText = `m. E. >50% (${committed.toFixed(1)}%) — aber erst ${confirmedPct.toFixed(1)}% bestätigt, es fehlen ${(50 - confirmedPct).toFixed(1)} Pp Bestätigungen`;
    statusCls = 'border-amber-500/40 bg-amber-600/10 text-amber-400';
  } else {
    statusText = `bestätigt: ${confirmedPct.toFixed(1)}% · m. E.: ${inPct.toFixed(1)}% — noch ${Math.max(50 - committed, 0).toFixed(1)} Pp bis >50% (m. E.)`;
    statusCls = 'border-border bg-secondary/40 text-muted-foreground';
  }

  return (
    <div className="bg-card border border-border rounded-sm p-4" data-testid="investor-appetite-card">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-foreground/60" />
          <p className="font-heading text-base text-foreground">Investor Appetite</p>
        </div>
        <div data-testid="appetite-status"
          className={`text-xs font-mono px-2 py-1 rounded-sm border ${statusCls}`}>
          {statusText}
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground mb-3">
        {isAdmin ? 'Klicken zum Durchschalten: ' : 'Status: '}
        <span className="text-green-400">grün = kommt m. E. rein</span> · <span className="text-green-950 bg-green-300 px-1 rounded-[2px]">hellgrün = hat bestätigt</span> · <span className="text-red-400">rot = raus</span> · grau = offen
      </p>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {INVESTORS.map((inv) => {
          const st = appetite[inv.key] || 'none';
          const ticket = ticketFmt(equity, inv.share);
          return (
            <button key={inv.key} onClick={() => toggle(inv)} disabled={!isAdmin}
              data-testid={`investor-badge-${inv.key}`} data-state={st}
              title={inv.note || inv.name}
              className={`flex items-center gap-1.5 border rounded-sm px-2 py-1 text-xs transition-colors ${BADGE_STYLES[st]} ${isAdmin ? 'cursor-pointer' : 'cursor-default'}`}>
              {st === 'in' && <Check className="w-3 h-3" />}
              {st === 'confirmed' && <CheckCheck className="w-3 h-3" />}
              {st === 'out' && <X className="w-3 h-3" />}
              <span className="font-medium">{inv.name}</span>
              <span className={`font-mono ${st === 'confirmed' ? 'text-green-900' : 'opacity-70'}`}>
                {inv.share.toLocaleString('de-DE')}%{ticket ? ` · ${ticket}` : ''}
              </span>
              {inv.note && <span className="text-[9px] opacity-60">2V</span>}
            </button>
          );
        })}
      </div>

      <div className="relative h-3 rounded-sm bg-secondary/60 overflow-hidden" data-testid="appetite-progress-bar">
        <div className="absolute inset-y-0 left-0 bg-green-600/70" style={{ width: `${Math.min(inPct, 100)}%` }} />
        <div className="absolute inset-y-0 bg-green-300" style={{ left: `${Math.min(inPct, 100)}%`, width: `${Math.min(confirmedPct, 100 - inPct)}%` }} />
        <div className="absolute inset-y-0 bg-red-600/50" style={{ left: `${Math.min(inPct + confirmedPct, 100)}%`, width: `${Math.min(outPct, 100 - inPct - confirmedPct)}%` }} />
        <div className="absolute inset-y-[-2px] w-[2px] bg-foreground" style={{ left: '50%' }} title="50%-Schwelle" />
      </div>
      <div className="mt-1.5 flex items-center gap-3 text-[11px] font-mono text-muted-foreground flex-wrap" data-testid="appetite-breakdown">
        <span className="text-green-400">■ m. E. rein: {inPct.toFixed(1)}%</span>
        <span className="text-green-300">■ bestätigt: {confirmedPct.toFixed(1)}%</span>
        <span className="text-red-400">■ raus: {outPct.toFixed(1)}%</span>
        <span>■ offen: {openPct.toFixed(1)}% ({openCount} Investoren)</span>
        {equity > 0 && committed > 0 && (
          <span className="text-green-400/80">≈ €{(equity * committed / 100).toFixed(2)}m Equity abgedeckt</span>
        )}
      </div>
    </div>
  );
}
